// Series CRUD interface. Mirrors api-teams.js — reads
// series/manifest.json in demo mode; future Supabase swap lands here
// without touching pages.

import { isConfigured } from './config.js';
import { loadSeries } from '../../shared/series-schema.js';

const SERIES_DIR = globalThis.__RM_SERIES_BASE__ ?? '../series/';
const MANIFEST_PATH = `${SERIES_DIR}manifest.json`;
let manifestCache = null;

export async function listSeries() {
  if (isConfigured()) throw new Error('Supabase client not implemented yet.');
  const manifest = await loadManifest();
  const summaries = await Promise.all(
    manifest.series.map(async filename => {
      try {
        const id = idFromFilename(filename);
        const data = await fetchSeriesFile(filename);
        return summarize(id, data);
      } catch (err) {
        console.error(`Failed to load series "${filename}":`, err);
        return null;
      }
    }),
  );
  return summaries.filter(Boolean);
}

export async function getSeries(id) {
  if (isConfigured()) throw new Error('Supabase client not implemented yet.');
  const manifest = await loadManifest();
  const filename = manifest.series.find(f => idFromFilename(f) === id);
  if (!filename) throw new Error(`Unknown series: ${id}`);
  const data = await fetchSeriesFile(filename);
  const { series } = loadSeries(data);
  return series;
}

async function loadManifest() {
  if (manifestCache) return manifestCache;
  // Cache-bust + no-store: without this, the browser's HTTP cache can
  // serve a stale manifest after files were renamed on disk, listing
  // filenames that 404 silently and leaving the list empty.
  const res = await fetch(`${MANIFEST_PATH}?t=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) throw new Error(`Failed to load series manifest: ${res.status}`);
  const data = await res.json();
  if (!Array.isArray(data?.series)) throw new Error('Series manifest is malformed: missing "series" array');
  manifestCache = data;
  return data;
}

async function fetchSeriesFile(filename) {
  const url = `${SERIES_DIR}${filename}?t=${Date.now()}`;
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function idFromFilename(filename) {
  return filename.replace(/\.series\.json$/, '').replace(/\.json$/, '');
}

function summarize(id, data) {
  // Surface the bits the race-setup picker needs without forcing a
  // second per-series fetch: name, shortName, class breakdown
  // (name + team count per class), total team count. Race-setup uses
  // total to gate "Start race"; the per-class breakdown shows up in
  // the picker row so the user can see "GT3: 8 teams · LMP2: 4 teams"
  // without drilling into the file.
  const rawClasses = Array.isArray(data.classes) ? data.classes : [];
  const classes = rawClasses
    .filter(c => c && typeof c === 'object')
    .map(c => ({
      id:        typeof c.id === 'string' ? c.id : '',
      name:      typeof c.name === 'string' ? c.name : '',
      teamCount: Array.isArray(c.teamIds) ? c.teamIds.length : 0,
    }));
  const teamCount = classes.reduce((acc, c) => acc + c.teamCount, 0);
  return {
    id,
    name:        data.name || id,
    shortName:   data.shortName || '',
    description: typeof data.description === 'string' ? data.description : '',
    classes,
    classCount:  classes.length,
    teamCount,
  };
}
